import { Bell, X } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { cn } from '@reflex/ui/lib/utils';
import { useAuth } from '@reflex/ui/client/context';
import { notificationHeadingFor } from '../notificationHeading.js';
import type { Notification } from '../../shared/types.js';

export interface NotificationInboxRowProps {
  notification: Notification;
  selected: boolean;
  onSelect: () => void;
  onDismiss: (id: string) => void;
}

/**
 * Compact one-line row used at the top of the inbox sidebar. Clicking
 * the row body fires `onSelect` so the host inbox view can render the
 * notification in the right pane — same interaction model as
 * `ActiveAgentRow` and `FlowRunSidebarRow`. The trailing X dismisses
 * without changing selection.
 */
export function NotificationInboxRow({
  notification,
  selected,
  onSelect,
  onDismiss,
}: NotificationInboxRowProps) {
  // The stored title/subtitle are written for the row's addressee. Reading
  // them straight would put an operator diagnosis — including the raw
  // failure reason — into this row's text *and* its `title` tooltip.
  const { title, subtitle } = notificationHeadingFor(notification, useAuth());

  return (
    // `min-w-0` on a flex item lets descendants honour `truncate`
    // (default flex `min-width: auto` would force the row to be at
    // least as wide as the longest unbreakable child). Paired with
    // the `w-full min-w-0` chain on the parent section, long titles
    // now clip with an ellipsis instead of pushing the row past the
    // sidebar's right edge.
    <div
      className={cn(
        'group flex w-full min-w-0 items-center gap-2 rounded-md border border-transparent',
        selected
          ? 'border-selected-border bg-selected text-selected-foreground'
          : 'border-border bg-card hover:bg-accent/40 transition-colors',
      )}
      data-testid={`notification-row-${notification.id}`}
      data-selected={selected || undefined}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-label={`Open notification: ${title}`}
        className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left focus:outline-none"
      >
        <Bell className="icon-sm shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-medium">{title}</span>
          {subtitle ? (
            <span className="truncate text-xs text-muted-foreground">{subtitle}</span>
          ) : null}
        </span>
      </button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label="Dismiss notification"
        onClick={() => onDismiss(notification.id)}
        className="mr-1 h-7 w-7 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
      >
        <X className="icon-sm" aria-hidden="true" />
      </Button>
    </div>
  );
}
